import {
  DirectDebitDetails,
  InsuranceName,
  PaymentDetailsData,
  PaymentFrequency,
} from 'scenes/common/types';

import {
  WireBindRequest,
  WirePayment,
  WirePaymentFrequency,
  WirePaymentMethod,
} from './types';
import { requestBindDefaults, wireProduct } from '../marshaller';

const toWirePaymentFrequency = (
  paymentFrequency: PaymentFrequency | undefined
): WirePaymentFrequency => {
  switch (paymentFrequency) {
    case PaymentFrequency.Annually:
      return WirePaymentFrequency.Annual;
    case PaymentFrequency.Monthly:
      return WirePaymentFrequency.Monthly;
    default:
      return WirePaymentFrequency.Unspecified;
  }
};

const marshalQuoteBind = (
  insuranceName: InsuranceName,
  policyNumber: string,
  policyReference: string,
  payment: WirePayment
): WireBindRequest => ({
  ...requestBindDefaults(wireProduct[insuranceName]),
  policyNumber,
  policyReference,
  payment,
});

export const toWireBpayPayment = (
  paymentDetails: Partial<PaymentDetailsData> | undefined
): WirePayment => ({
  frequency: toWirePaymentFrequency(paymentDetails?.paymentFrequency),
  method: WirePaymentMethod.BPAY,
  date: paymentDetails?.paymentDate || '',
});

export const toWireDirectDebitPayment = (
  paymentDetails: Partial<PaymentDetailsData> | undefined,
  directDebitDetails: DirectDebitDetails
): WirePayment => {
  const {
    accountNumber,
    accountName,
    bsbDetails: { bsb, bankName, branchPostcode, branchAddress },
  } = directDebitDetails;
  return {
    frequency: toWirePaymentFrequency(paymentDetails?.paymentFrequency),
    method: WirePaymentMethod.DebitCard,
    date: paymentDetails?.paymentDate || '',
    bsbDetails: {
      number: bsb,
      accountNumber,
      accountName: accountName.trim(),
      bankName,
      branchPostcode,
      branchAddress,
    },
  };
};

export const toWireCreditCardPayment = (
  paymentDetails: Partial<PaymentDetailsData> | undefined,
  singleUseToken: string
): WirePayment => ({
  frequency: toWirePaymentFrequency(paymentDetails?.paymentFrequency),
  method: WirePaymentMethod.CreditCard,
  date: paymentDetails?.paymentDate || '',
  ccDetails: { singleUseToken },
});

export const marshalBindBpayPayment = (
  insuranceName: InsuranceName,
  policyNumber: string,
  policyReference: string,
  paymentDetails: PaymentDetailsData
) =>
  marshalQuoteBind(
    insuranceName,
    policyNumber,
    policyReference,
    toWireBpayPayment(paymentDetails)
  );

export const marshalBindDirectDebitPayment = (
  insuranceName: InsuranceName,
  policyNumber: string,
  policyReference: string,
  paymentDetails: PaymentDetailsData,
  directDebitDetails: DirectDebitDetails
) =>
  marshalQuoteBind(
    insuranceName,
    policyNumber,
    policyReference,
    toWireDirectDebitPayment(paymentDetails, directDebitDetails)
  );

export const marshalBindCreditCardPayment = (
  insuranceName: InsuranceName,
  policyNumber: string,
  policyReference: string,
  paymentDetails: PaymentDetailsData,
  singleUseToken: string
) =>
  marshalQuoteBind(
    insuranceName,
    policyNumber,
    policyReference,
    toWireCreditCardPayment(paymentDetails, singleUseToken)
  );
